import type { FontMapDto } from '@editar-pdf/api';
import type { DocumentFontEntry, DocumentFontRegistry } from './documentFonts';

/**
 * The human-readable name of a committed `fontName`, as the font picker spells it.
 *
 * A committed name is either a pdf.js internal name of a document-embedded face
 * (`g_d0_f7`) or a catalog font id (`noto-sans`) — never both, so the registry is
 * tried first and the catalog second without either shadowing the other. The raw
 * name is the last resort: it covers a font committed before the catalog list or
 * the page's faces had loaded, where a raw id still beats reporting nothing.
 *
 * Presentation-only. Nothing may resolve a font through this — labels are shared
 * by every subset of one face and by faces of the same name across tiers.
 */
export const fontLabel = (
  fontName: string,
  documentFonts: DocumentFontRegistry,
  knownFonts: FontMapDto[] | null,
): string => {
  // The Record type implies a hit for every key; a registry only holds the faces parsed
  // so far, so the lookup is annotated for what it actually returns.
  const embedded = documentFonts[fontName] as DocumentFontEntry | undefined;
  if (embedded) return embedded.label;

  const known = (knownFonts ?? []).find((font) => font.id === fontName);

  return known ? known.name : fontName;
};
